import clsx from 'clsx'
import React, { useEffect, useState } from 'react'

import { Text } from '@wartek-id/text'

import { determineTopicState } from 'app/PelatihanMandiriPage/helper'

import type { OverallProgress } from 'types/microlearning'

import Check from 'assets/icons/check-fat.svg'

interface ProgressionBarProps extends React.HTMLAttributes<HTMLDivElement> {
  progressData: OverallProgress
}

export const ProgressionIndicator = ({ currentStep = 1, threshold = 1 }) => {
  return (
    <div
      data-testid={
        currentStep > threshold
          ? 'done'
          : currentStep === threshold
          ? 'current'
          : 'not started'
      }
      className={clsx(
        'flex items-center justify-center mb-2 w-5 h-5 rounded-full',
        currentStep > threshold
          ? 'bg-icon-success'
          : currentStep === threshold
          ? 'bg-gray-5 border-2 border-interactive-focused'
          : 'bg-gray-5 border-2 border-gray-20'
      )}
    >
      {currentStep > threshold ? (
        <Check />
      ) : currentStep === threshold ? (
        <div className="w-3 h-3 bg-interactive-focused rounded-full">
          &nbsp;
        </div>
      ) : null}
    </div>
  )
}

const ProgressionBar = ({ progressData }: ProgressionBarProps) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [currentValueText, setCurrentValueText] = useState(1)
  const currentTopicState = determineTopicState(progressData)
  useEffect(() => {
    let step, valueText
    switch (currentTopicState) {
      default:
      case 'DEFAULT':
        step = 1
        valueText = 'Mulai Modul'
        break
      case 'MODULE_ON_PROGRESS':
        step = 2
        valueText = 'Modul Selesai'
        break
      case 'AKSI_NYATA_CREATED':
      case 'AKSI_NYATA_ON_PROGRESS':
      case 'MODULE_DONE':
        step = 3
        valueText = 'Praktik Aksi Nyata'
        break
      case 'AKSI_NYATA_DONE':
        step = 5
        valueText = 'Pelatihan Selesai'
        break
    }
    setCurrentValueText(valueText)
    setCurrentStep(step)
  }, [currentTopicState])

  return (
    <>
      <div
        role="progressbar"
        aria-valuemax={5}
        aria-valuemin={1}
        aria-valuenow={currentStep}
        aria-valuetext={`${currentValueText}`}
        className="relative h-12 w-full"
      >
        <div className="absolute flex flex-wrap justify-center w-16 -ml-8 left-0 top-0 z-10">
          <ProgressionIndicator currentStep={currentStep} threshold={1} />
          <Text className="w-full text-center" as="div" variant="helper">
            Mulai Modul
          </Text>
        </div>

        <div
          className={clsx(
            'absolute w-1/3 ml-1.5 left-0 top-2 h-[2px] z-0',
            currentStep > 1 ? 'bg-gray-50' : 'bg-gray-30'
          )}
        >
          &nbsp;
        </div>

        <div className="absolute flex flex-wrap justify-center w-16 -ml-8 left-1/3 top-0 z-10">
          <ProgressionIndicator currentStep={currentStep} threshold={2} />
          <Text className="w-full text-center" as="div" variant="helper">
            Modul Selesai
          </Text>
        </div>

        <div
          className={clsx(
            'absolute w-1/3 ml-1.5 left-1/3 top-2 h-[2px] z-0',
            currentStep > 2 ? 'bg-gray-50' : 'bg-gray-30'
          )}
        >
          &nbsp;
        </div>

        <div className="absolute flex flex-wrap justify-center w-16 -ml-8 left-2/3 top-0 z-10">
          <ProgressionIndicator currentStep={currentStep} threshold={3} />
          <Text className="w-full text-center" as="div" variant="helper">
            Praktik Aksi Nyata
          </Text>
        </div>

        <div
          className={clsx(
            'absolute w-1/3 ml-1.5 left-2/3 top-2 h-[2px] z-0',
            currentStep > 3 ? 'bg-gray-50' : 'bg-gray-30'
          )}
        >
          &nbsp;
        </div>

        <div className="absolute flex flex-wrap justify-center w-16 right-0 -mr-8 top-0 z-10">
          <ProgressionIndicator currentStep={currentStep} threshold={4} />
          <Text className="w-full text-center" as="div" variant="helper">
            Pelatihan Selesai
          </Text>
        </div>
      </div>
    </>
  )
}

export default ProgressionBar
